import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo } from "react";
import { getNetworkInfoThunk } from "tm-shared/api";
import { Contract, Market } from "tm-shared/types";

import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { getFileUrl } from "@/utils/file/getFileUrl";
import { handleDownloadFile } from "@/utils/file/handleDownloadFile";

import {
    StyledAPContractTable,
    StyledAPContractTableFile,
    StyledAPContractTableFileIcon,
    StyledAPContractTableFileName,
    StyledAPContractTableRow,
    StyledAPContractTableSplittedBlock,
    StyledAPContractTableSplittedRow,
} from "../../../Styled";
import { ContractAdditionalInfo } from "./ContractAdditionalInfo";

const DATE_FORMAT = "DD.MM.YY";

interface ContractViewProps {
    contract: Contract;
    fopName: string;
    contractTypeName: string;
    market: Market;
}

export const ContractView: React.FC<ContractViewProps> = ({
    contract,
    fopName,
    contractTypeName,
    market,
}) => {
    const { regions, networks } = useAppSelector((s) => s.user);

    const dispatch = useAppDispatch();

    const startDate = useMemo(
        () => dayjs(contract.startDate).format(DATE_FORMAT),
        [contract.startDate],
    );

    const endDate = useMemo(
        () =>
            contract.endDate
                ? dayjs(contract.endDate).format(DATE_FORMAT)
                : "Безстроково",
        [contract.endDate],
    );

    const getTargetTypeInfo = useCallback(
        (type: string): string => {
            const { contragent, address, storage, networkId } = market ?? {};

            const mapped: Record<string, string> = {
                contragent: `Контрагент (${
                    contragent?.name || "Невідома юр.особа"
                })`,
                mainRegion: `Область (${
                    regions?.[address?.mainRegionId || ""] ?? "Невідомий регіон"
                })`,
                storage: `Склад (${storage?.name || "Невідомий склад"})`,
                network: `Мережа (${
                    networks?.[networkId || ""] ?? "Невідома мережа"
                })`,
            };

            return mapped[type] || "ТТ";
        },
        [market, regions, networks],
    );

    const handleFileClick = useCallback((file: string) => {
        const fileName = file.split("/").pop() || "document";
        handleDownloadFile(getFileUrl(file), fileName.replace(/\..+$/, ""));
    }, []);

    const dateBlocks = useMemo(
        () => [
            { label: "Дата початку:", value: startDate },
            { label: "Дата завершення:", value: endDate },
        ],
        [startDate, endDate],
    );

    useEffect(() => {
        if (market?.networkId && !networks?.[market.networkId]) {
            dispatch(getNetworkInfoThunk({ network: market.networkId }));
        }
    }, []);

    return (
        <StyledAPContractTable>
            <StyledAPContractTableRow>
                <PrimaryText fontSize={16}>{fopName}</PrimaryText>
                {contract?.description && (
                    <SecondaryText>{contract.description}</SecondaryText>
                )}
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <StyledAPContractTableSplittedRow>
                    {dateBlocks.map(({ label, value }) => (
                        <StyledAPContractTableSplittedBlock key={label}>
                            <SecondaryText>{label}</SecondaryText>
                            <PrimaryText fontSize={16}>{value}</PrimaryText>
                        </StyledAPContractTableSplittedBlock>
                    ))}
                </StyledAPContractTableSplittedRow>
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <SecondaryText>Тип контракту:</SecondaryText>
                <PrimaryText fontSize={16}>{contractTypeName}</PrimaryText>
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <SecondaryText>Рівень дії:</SecondaryText>
                <PrimaryText fontSize={16}>
                    {getTargetTypeInfo(contract.targetType)}
                </PrimaryText>
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <SecondaryText>Документи:</SecondaryText>

                {contract.files.length > 0 ? (
                    contract.files.map((file) => {
                        const fileName = file.split("/").pop()!;
                        return (
                            <StyledAPContractTableFile
                                key={file}
                                onClick={() => handleFileClick(file)}
                            >
                                <StyledAPContractTableFileIcon />
                                <StyledAPContractTableFileName>
                                    {fileName}
                                </StyledAPContractTableFileName>
                            </StyledAPContractTableFile>
                        );
                    })
                ) : (
                    <PrimaryText fontSize={16}>Файли відсутні</PrimaryText>
                )}
            </StyledAPContractTableRow>

            <ContractAdditionalInfo contract={contract} />
        </StyledAPContractTable>
    );
};
